import { IQboCustomerMappingInput } from '@common/interfaces/qbo'
import { QboDataset } from '@common/interfaces/qboReporting'
import { IApi } from '../../api'
import {
  IQboGetDocumentsParams,
  IQboListTransactionsParams,
  IQboPullReportDataParams,
  IQboSettingsUpdate,
} from '../../api/qbo'
import { createAction } from '../types'

export const prefix = 'qbo'

export const GET_CONNECT_URL_REQUEST = `${prefix}/GET_CONNECT_URL_REQUEST`
export const GET_CONNECT_URL_SUCCESS = `${prefix}/GET_CONNECT_URL_SUCCESS`
export const GET_CONNECT_URL_FAILURE = `${prefix}/GET_CONNECT_URL_FAILURE`

export const getConnectUrl = createAction((clientId: string) => ({
  type: [GET_CONNECT_URL_REQUEST, GET_CONNECT_URL_SUCCESS, GET_CONNECT_URL_FAILURE],
  promise: (api: IApi) => api.qbo.getConnectUrl(clientId),
}))

export const GET_CONNECTION_REQUEST = `${prefix}/GET_CONNECTION_REQUEST`
export const GET_CONNECTION_SUCCESS = `${prefix}/GET_CONNECTION_SUCCESS`
export const GET_CONNECTION_FAILURE = `${prefix}/GET_CONNECTION_FAILURE`

export const getConnection = createAction((clientId: string) => ({
  type: [GET_CONNECTION_REQUEST, GET_CONNECTION_SUCCESS, GET_CONNECTION_FAILURE],
  promise: (api: IApi) => api.qbo.getConnection(clientId),
}))

export const UPDATE_SETTINGS_REQUEST = `${prefix}/UPDATE_SETTINGS_REQUEST`
export const UPDATE_SETTINGS_SUCCESS = `${prefix}/UPDATE_SETTINGS_SUCCESS`
export const UPDATE_SETTINGS_FAILURE = `${prefix}/UPDATE_SETTINGS_FAILURE`

export const updateConnectionSettings = createAction(
  (clientId: string, settings: IQboSettingsUpdate) => ({
    type: [UPDATE_SETTINGS_REQUEST, UPDATE_SETTINGS_SUCCESS, UPDATE_SETTINGS_FAILURE],
    promise: (api: IApi) => api.qbo.updateConnectionSettings(clientId, settings),
  }),
)

export const LIST_ACCOUNTS_REQUEST = `${prefix}/LIST_ACCOUNTS_REQUEST`
export const LIST_ACCOUNTS_SUCCESS = `${prefix}/LIST_ACCOUNTS_SUCCESS`
export const LIST_ACCOUNTS_FAILURE = `${prefix}/LIST_ACCOUNTS_FAILURE`

export const listAccounts = createAction((clientId: string) => ({
  type: [LIST_ACCOUNTS_REQUEST, LIST_ACCOUNTS_SUCCESS, LIST_ACCOUNTS_FAILURE],
  promise: (api: IApi) => api.qbo.listAccounts(clientId),
}))

export const DISCONNECT_REQUEST = `${prefix}/DISCONNECT_REQUEST`
export const DISCONNECT_SUCCESS = `${prefix}/DISCONNECT_SUCCESS`
export const DISCONNECT_FAILURE = `${prefix}/DISCONNECT_FAILURE`

export const disconnect = createAction((id: string) => ({
  type: [DISCONNECT_REQUEST, DISCONNECT_SUCCESS, DISCONNECT_FAILURE],
  promise: (api: IApi) => api.qbo.disconnect(id),
}))

export const REFRESH_CACHE_REQUEST = `${prefix}/REFRESH_CACHE_REQUEST`
export const REFRESH_CACHE_SUCCESS = `${prefix}/REFRESH_CACHE_SUCCESS`
export const REFRESH_CACHE_FAILURE = `${prefix}/REFRESH_CACHE_FAILURE`

export const refreshCache = createAction((clientId: string) => ({
  type: [REFRESH_CACHE_REQUEST, REFRESH_CACHE_SUCCESS, REFRESH_CACHE_FAILURE],
  promise: (api: IApi) => api.qbo.refreshCache(clientId),
}))

export const GET_CUSTOMER_MAPPINGS_REQUEST = `${prefix}/GET_CUSTOMER_MAPPINGS_REQUEST`
export const GET_CUSTOMER_MAPPINGS_SUCCESS = `${prefix}/GET_CUSTOMER_MAPPINGS_SUCCESS`
export const GET_CUSTOMER_MAPPINGS_FAILURE = `${prefix}/GET_CUSTOMER_MAPPINGS_FAILURE`

export interface IListCustomerMappingsParams {
  clientId: string
  page?: number
  perPage?: number
  search?: string
  orderBy?: 'entityName' | 'qboCustomerName'
  orderDirection?: 'ASC' | 'DESC'
  countOnly?: boolean
  loadMore?: boolean
}

export const getCustomerMappings = createAction((params: IListCustomerMappingsParams) => ({
  type: [
    GET_CUSTOMER_MAPPINGS_REQUEST,
    GET_CUSTOMER_MAPPINGS_SUCCESS,
    GET_CUSTOMER_MAPPINGS_FAILURE,
  ],
  promise: (api: IApi) => api.qbo.getCustomerMappings(params),
  params, // CRITICAL: drives loadMore append in createLoadingDataReducer
}))

export const SEARCH_CUSTOMERS_REQUEST = `${prefix}/SEARCH_CUSTOMERS_REQUEST`
export const SEARCH_CUSTOMERS_SUCCESS = `${prefix}/SEARCH_CUSTOMERS_SUCCESS`
export const SEARCH_CUSTOMERS_FAILURE = `${prefix}/SEARCH_CUSTOMERS_FAILURE`

export const searchCustomers = createAction(
  (params: { clientId: string; search?: string; limit?: number }) => ({
    type: [SEARCH_CUSTOMERS_REQUEST, SEARCH_CUSTOMERS_SUCCESS, SEARCH_CUSTOMERS_FAILURE],
    promise: (api: IApi) => api.qbo.searchCustomers(params),
  }),
)

export const SAVE_CUSTOMER_MAPPINGS_REQUEST = `${prefix}/SAVE_CUSTOMER_MAPPINGS_REQUEST`
export const SAVE_CUSTOMER_MAPPINGS_SUCCESS = `${prefix}/SAVE_CUSTOMER_MAPPINGS_SUCCESS`
export const SAVE_CUSTOMER_MAPPINGS_FAILURE = `${prefix}/SAVE_CUSTOMER_MAPPINGS_FAILURE`

export const saveCustomerMappings = createAction(
  (clientId: string, mappings: IQboCustomerMappingInput[]) => ({
    type: [
      SAVE_CUSTOMER_MAPPINGS_REQUEST,
      SAVE_CUSTOMER_MAPPINGS_SUCCESS,
      SAVE_CUSTOMER_MAPPINGS_FAILURE,
    ],
    promise: (api: IApi) => api.qbo.saveCustomerMappings(clientId, mappings),
    // Read by the reducer to apply the saved rows to the loaded list.
    params: { clientId, mappings },
  }),
)

export const LOG_COLLECTIONS_REQUEST = `${prefix}/LOG_COLLECTIONS_REQUEST`
export const LOG_COLLECTIONS_SUCCESS = `${prefix}/LOG_COLLECTIONS_SUCCESS`
export const LOG_COLLECTIONS_FAILURE = `${prefix}/LOG_COLLECTIONS_FAILURE`

export const logCollections = createAction(
  (data: {
    clientId: string
    recordDateFrom: string
    recordDateTo: string
    activityIds?: string[]
    dryRun?: boolean
  }) => ({
    type: [LOG_COLLECTIONS_REQUEST, LOG_COLLECTIONS_SUCCESS, LOG_COLLECTIONS_FAILURE],
    promise: (api: IApi) => api.qbo.logCollections(data),
  }),
)

export const CLEAR_COLLECTIONS_REQUEST = `${prefix}/CLEAR_COLLECTIONS_REQUEST`
export const CLEAR_COLLECTIONS_SUCCESS = `${prefix}/CLEAR_COLLECTIONS_SUCCESS`
export const CLEAR_COLLECTIONS_FAILURE = `${prefix}/CLEAR_COLLECTIONS_FAILURE`

export const clearCollections = createAction(
  (data: { clientId: string; clearSinceDate?: string; activityTypes?: string[] }) => ({
    type: [CLEAR_COLLECTIONS_REQUEST, CLEAR_COLLECTIONS_SUCCESS, CLEAR_COLLECTIONS_FAILURE],
    promise: (api: IApi) => api.qbo.clearCollections(data),
  }),
)

export const DISMISS_COLLECTIONS_REQUEST = `${prefix}/DISMISS_COLLECTIONS_REQUEST`
export const DISMISS_COLLECTIONS_SUCCESS = `${prefix}/DISMISS_COLLECTIONS_SUCCESS`
export const DISMISS_COLLECTIONS_FAILURE = `${prefix}/DISMISS_COLLECTIONS_FAILURE`

export const dismissCollections = createAction((clientId: string, activityIds: string[]) => ({
  type: [DISMISS_COLLECTIONS_REQUEST, DISMISS_COLLECTIONS_SUCCESS, DISMISS_COLLECTIONS_FAILURE],
  promise: (api: IApi) => api.qbo.dismissCollections(clientId, activityIds),
}))

export const LIST_COLLECTION_LOG_REQUEST = `${prefix}/LIST_COLLECTION_LOG_REQUEST`
export const LIST_COLLECTION_LOG_SUCCESS = `${prefix}/LIST_COLLECTION_LOG_SUCCESS`
export const LIST_COLLECTION_LOG_FAILURE = `${prefix}/LIST_COLLECTION_LOG_FAILURE`

export const listCollectionLog = createAction(
  (params: {
    clientId: string
    page?: number
    perPage?: number
    loadMore?: boolean
    filters?: Record<string, unknown>
    orderBy?: string
    orderDirection?: 'ASC' | 'DESC'
  }) => ({
    type: [LIST_COLLECTION_LOG_REQUEST, LIST_COLLECTION_LOG_SUCCESS, LIST_COLLECTION_LOG_FAILURE],
    promise: (api: IApi) => api.qbo.listCollectionLog(params),
    params, // CRITICAL: drives loadMore append in createLoadingDataReducer
  }),
)

export const PULL_REPORT_DATA_REQUEST = `${prefix}/PULL_REPORT_DATA_REQUEST`
export const PULL_REPORT_DATA_SUCCESS = `${prefix}/PULL_REPORT_DATA_SUCCESS`
export const PULL_REPORT_DATA_FAILURE = `${prefix}/PULL_REPORT_DATA_FAILURE`

// Correlation id for a pull, stamped here so every caller is correlated for free.
// A pull runs for minutes and nothing cancels it, so completions routinely arrive
// out of order and under a different client than the one that asked; the reducer
// admits only the completion carrying the id of the pull still in flight.
let pullRequestCounter = 0
const nextPullRequestId = () => {
  pullRequestCounter += 1
  return pullRequestCounter
}

export interface IQboPullParams {
  clientId?: string
  requestId?: number
}

export const pullReportData = createAction((params: IQboPullReportDataParams) => ({
  type: [PULL_REPORT_DATA_REQUEST, PULL_REPORT_DATA_SUCCESS, PULL_REPORT_DATA_FAILURE],
  // `params` carries the correlation id, the request body deliberately does not.
  promise: (api: IApi) => api.qbo.pullReportData(params),
  params: { ...params, requestId: nextPullRequestId() },
}))

export const EXPORT_REPORTS_REQUEST = `${prefix}/EXPORT_REPORTS_REQUEST`
export const EXPORT_REPORTS_SUCCESS = `${prefix}/EXPORT_REPORTS_SUCCESS`
export const EXPORT_REPORTS_FAILURE = `${prefix}/EXPORT_REPORTS_FAILURE`

export const exportReports = createAction(
  (params: { clientId: string; datasets: QboDataset[] }) => ({
    type: [EXPORT_REPORTS_REQUEST, EXPORT_REPORTS_SUCCESS, EXPORT_REPORTS_FAILURE],
    promise: (api: IApi) => api.qbo.exportReports(params),
  }),
)

export const SUBMIT_REPORTING_REQUEST = `${prefix}/SUBMIT_REPORTING_REQUEST`
export const SUBMIT_REPORTING_SUCCESS = `${prefix}/SUBMIT_REPORTING_SUCCESS`
export const SUBMIT_REPORTING_FAILURE = `${prefix}/SUBMIT_REPORTING_FAILURE`

export const submitReporting = createAction((params: { clientId: string }) => ({
  type: [SUBMIT_REPORTING_REQUEST, SUBMIT_REPORTING_SUCCESS, SUBMIT_REPORTING_FAILURE],
  promise: (api: IApi) => api.qbo.submitReporting(params),
}))

export const LIST_TRANSACTIONS_REQUEST = `${prefix}/LIST_TRANSACTIONS_REQUEST`
export const LIST_TRANSACTIONS_SUCCESS = `${prefix}/LIST_TRANSACTIONS_SUCCESS`
export const LIST_TRANSACTIONS_FAILURE = `${prefix}/LIST_TRANSACTIONS_FAILURE`

export const listTransactions = createAction(
  (params: IQboListTransactionsParams & { loadMore?: boolean }) => ({
    type: [LIST_TRANSACTIONS_REQUEST, LIST_TRANSACTIONS_SUCCESS, LIST_TRANSACTIONS_FAILURE],
    promise: (api: IApi) => api.qbo.listTransactions(params),
    params, // CRITICAL: drives loadMore append in createLoadingDataReducer
  }),
)

export const SYNC_TRANSACTIONS_REQUEST = `${prefix}/SYNC_TRANSACTIONS_REQUEST`
export const SYNC_TRANSACTIONS_SUCCESS = `${prefix}/SYNC_TRANSACTIONS_SUCCESS`
export const SYNC_TRANSACTIONS_FAILURE = `${prefix}/SYNC_TRANSACTIONS_FAILURE`

export const syncTransactions = createAction((data: { clientId: string }) => ({
  type: [SYNC_TRANSACTIONS_REQUEST, SYNC_TRANSACTIONS_SUCCESS, SYNC_TRANSACTIONS_FAILURE],
  promise: (api: IApi) => api.qbo.syncTransactions(data),
}))

export const GET_DOCUMENTS_REQUEST = `${prefix}/GET_DOCUMENTS_REQUEST`
export const GET_DOCUMENTS_SUCCESS = `${prefix}/GET_DOCUMENTS_SUCCESS`
export const GET_DOCUMENTS_FAILURE = `${prefix}/GET_DOCUMENTS_FAILURE`

export const getDocuments = createAction((params: IQboGetDocumentsParams) => ({
  type: [GET_DOCUMENTS_REQUEST, GET_DOCUMENTS_SUCCESS, GET_DOCUMENTS_FAILURE],
  promise: (api: IApi) => api.qbo.getDocuments(params),
  params,
}))

export const RESET_PULL_STATE = `${prefix}/RESET_PULL_STATE`

// The pull result and the filing it produced belong to one client, but the slice
// is global. Dispatched when the panel leaves a client so the next one does not
// inherit them.
export const resetPullState = () => ({
  type: RESET_PULL_STATE,
})
